/**
 * Charge Accounts Page
 * Manage revolving credit accounts (credit cards, store cards, business lines).
 * Each active account auto-generates an interest expense row.
 */

import { Fragment, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { formatCurrency } from '@/lib/utils';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  CreditCard,
  Plus,
  Search,
  Loader2,
  DollarSign,
  Percent,
  Wallet,
  Hash,
  ChevronDown,
  ChevronRight,
  AlertCircle,
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface ChargeAccount {
  id: string;
  businessProfileId: string | null;
  businessProfileName: string | null;
  name: string;
  issuer: string | null;
  description: string | null;
  accountType: string;
  creditLimit: number;
  currentBalance: number;
  interestRate: number;
  minimumPayment: number;
  desiredPayment: number;
  paymentDueDay: number;
  statementCloseDay: number | null;
  status: string;
  autopayEnabled: boolean;
  isInterestDeductible: boolean;
  last4: string | null;
  notes: string | null;
}

interface BusinessProfile {
  id: string;
  name: string;
}

interface ChargeAccountOverviewData {
  totalCount: number;
  activeCount: number;
  totalBalance: number;
  totalCreditLimit: number;
  totalDesiredPayment: number;
  totalMinimumPayment: number;
  totalMonthlyInterest: number;
}

interface CardLoadRow {
  id: string;
  name: string;
  issuer: string | null;
  last4: string | null;
  accountType: string;
  creditLimit: number;
  currentBalance: number;
  interestRate: number;
  paymentDueDay: number;
  totalMonthlyLoad: number;
  allocationCount: number;
  subscriptionCount: number;
  subscriptionMonthly: number;
  expenseCount: number;
  expenseMonthly: number;
  monthlyInterest: number;
}

const statusVariant: Record<string, 'default' | 'success' | 'warning' | 'destructive' | 'secondary'> = {
  active: 'success',
  closed: 'secondary',
  frozen: 'destructive',
};

const accountTypeLabel: Record<string, string> = {
  credit_card: 'Credit Card',
  store_card: 'Store Card',
  business_line: 'Business Line',
};

interface AllocationRow {
  id: string;
  kind: 'subscription' | 'expense';
  name: string;
  vendor: string | null;
  categoryName: string | null;
  amount: number;
  frequency: string | null;
  paymentDueDay: number | null;
  monthlyCost: number;
}

function AllocationsPanel({ accountId, creditLimit }: { accountId: string; creditLimit: number }) {
  const { data, isLoading } = useQuery({
    queryKey: ['chargeAccountAllocations', accountId],
    queryFn: () => api.getChargeAccountAllocations(accountId),
  });

  const allocations = (data?.data?.allocations || []) as AllocationRow[];
  const total = data?.data?.summary.totalMonthlyAllocated || 0;
  const pctOfLimit = creditLimit > 0 ? (total / creditLimit) * 100 : 0;

  if (isLoading) {
    return (
      <div className="p-6 flex justify-center">
        <Loader2 className="h-4 w-4 animate-spin" />
      </div>
    );
  }

  if (allocations.length === 0) {
    return (
      <div className="p-6 text-sm text-muted-foreground text-center">
        No recurring bills allocated to this card yet.
        Edit a subscription or a recurring common expense and pick this card under "Paid with".
      </div>
    );
  }

  return (
    <div className="p-4 space-y-3 bg-muted/20">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium">Recurring charges allocated</p>
          <p className="text-xs text-muted-foreground">
            {allocations.length} item{allocations.length !== 1 ? 's' : ''} &middot;{' '}
            {formatCurrency(total)} / month
            {creditLimit > 0 && ` (${pctOfLimit.toFixed(1)}% of limit)`}
          </p>
        </div>
      </div>
      <table className="w-full text-sm bg-background rounded border">
        <thead>
          <tr className="border-b bg-muted/30">
            <th className="text-left p-2 text-xs font-medium">Bill</th>
            <th className="text-left p-2 text-xs font-medium">Type</th>
            <th className="text-left p-2 text-xs font-medium">Frequency</th>
            <th className="text-center p-2 text-xs font-medium">Due Day</th>
            <th className="text-right p-2 text-xs font-medium">Amount</th>
            <th className="text-right p-2 text-xs font-medium">Monthly</th>
          </tr>
        </thead>
        <tbody>
          {allocations.map((a) => (
            <tr key={`${a.kind}-${a.id}`} className="border-b last:border-b-0">
              <td className="p-2">
                <div className="font-medium">{a.name}</div>
                {a.vendor && <div className="text-xs text-muted-foreground">{a.vendor}</div>}
              </td>
              <td className="p-2">
                <Badge variant="secondary" className="text-xs">
                  {a.kind === 'subscription' ? 'Subscription' : (a.categoryName || 'Expense')}
                </Badge>
              </td>
              <td className="p-2 text-xs capitalize">{a.frequency?.replace('_', ' ') || '—'}</td>
              <td className="p-2 text-center text-xs">{a.paymentDueDay ?? '—'}</td>
              <td className="p-2 text-right">{formatCurrency(a.amount)}</td>
              <td className="p-2 text-right font-medium">{formatCurrency(a.monthlyCost)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="bg-muted/30">
            <td colSpan={5} className="p-2 text-right text-xs font-medium">Total monthly</td>
            <td className="p-2 text-right font-bold">{formatCurrency(total)}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

export function ChargeAccountsPage() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [profileFilter, setProfileFilter] = useState('');
  const [page, setPage] = useState(1);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState<ChargeAccount | null>(null);
  const [expandedAccountId, setExpandedAccountId] = useState<string | null>(null);

  const { data: profilesData } = useQuery({
    queryKey: ['businessProfiles'],
    queryFn: () => api.getBusinessProfiles(),
  });

  const { data: overviewData } = useQuery({
    queryKey: ['chargeAccountOverview', { businessProfileId: profileFilter }],
    queryFn: () => api.getChargeAccountOverview({
      businessProfileId: profileFilter || undefined,
    }),
  });

  // Per-card monthly load: roll-up of recurring bills + subscriptions assigned
  // to each active card. Backs the Monthly Card Load planning section so the
  // user can see "AMEX is loaded $4k/mo, headroom $26k" at a glance.
  const { data: cardLoadData } = useQuery({
    queryKey: ['cardLoadSummary', { businessProfileId: profileFilter }],
    queryFn: () => api.getCardLoadSummary({
      businessProfileId: profileFilter || undefined,
    }),
  });

  const { data, isLoading } = useQuery({
    queryKey: ['chargeAccounts', { page, search, status: statusFilter, accountType: typeFilter, businessProfileId: profileFilter }],
    queryFn: () => api.getChargeAccounts({
      page,
      limit: 100,
      search: search || undefined,
      status: statusFilter || undefined,
      accountType: typeFilter || undefined,
      businessProfileId: profileFilter || undefined,
    }),
  });

  const profiles = (profilesData?.data || []) as BusinessProfile[];
  const overview = overviewData?.data as ChargeAccountOverviewData | undefined;
  const accounts = (data?.data || []) as ChargeAccount[];
  const cardLoads = (cardLoadData?.data || []) as CardLoadRow[];
  const totalCardLoad = cardLoads.reduce((s, c) => s + c.totalMonthlyLoad, 0);

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: ['chargeAccounts'] });
    queryClient.invalidateQueries({ queryKey: ['chargeAccountOverview'] });
    queryClient.invalidateQueries({ queryKey: ['chargeAccountAllocations'] });
    queryClient.invalidateQueries({ queryKey: ['cardLoadSummary'] });
    queryClient.invalidateQueries({ queryKey: ['expenses'] });
    queryClient.invalidateQueries({ queryKey: ['expenseSummary'] });
    queryClient.invalidateQueries({ queryKey: ['upcomingPayments'] });
  };

  const createMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => api.createChargeAccount(data),
    onSuccess: () => {
      invalidateAll();
      setDialogOpen(false);
      toast({ title: 'Charge account created' });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) => api.updateChargeAccount(id, data),
    onSuccess: () => {
      invalidateAll();
      setDialogOpen(false);
      setEditingAccount(null);
      toast({ title: 'Charge account updated' });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteChargeAccount(id),
    onSuccess: () => {
      invalidateAll();
      toast({ title: 'Charge account deleted' });
    },
  });

  function openCreate() {
    setEditingAccount(null);
    setDialogOpen(true);
  }

  function openEdit(account: ChargeAccount) {
    setEditingAccount(account);
    setDialogOpen(true);
  }

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const payload: Record<string, unknown> = {
      name: formData.get('name'),
      issuer: formData.get('issuer') || null,
      description: formData.get('description') || null,
      businessProfileId: formData.get('businessProfileId') || null,
      accountType: formData.get('accountType') || 'credit_card',
      creditLimit: parseFloat(formData.get('creditLimit') as string) || 0,
      currentBalance: parseFloat(formData.get('currentBalance') as string) || 0,
      interestRate: parseFloat(formData.get('interestRate') as string) || 0,
      minimumPayment: parseFloat(formData.get('minimumPayment') as string) || 0,
      desiredPayment: parseFloat(formData.get('desiredPayment') as string) || 0,
      paymentDueDay: parseInt(formData.get('paymentDueDay') as string) || 1,
      statementCloseDay: formData.get('statementCloseDay') ? parseInt(formData.get('statementCloseDay') as string) : null,
      status: formData.get('status') || 'active',
      autopayEnabled: formData.get('autopayEnabled') === 'on',
      isInterestDeductible: formData.get('isInterestDeductible') === 'on',
      last4: formData.get('last4') || null,
      notes: formData.get('notes') || null,
    };

    if (editingAccount) {
      updateMutation.mutate({ id: editingAccount.id, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  }

  const utilization = overview && overview.totalCreditLimit > 0
    ? ((overview.totalBalance / overview.totalCreditLimit) * 100).toFixed(1)
    : '0.0';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-end">
        <Button onClick={openCreate}>
          <Plus className="h-4 w-4 mr-2" />
          New Charge Account
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-red-500/10"><DollarSign className="h-5 w-5 text-red-500" /></div>
              <div>
                <p className="text-sm text-muted-foreground">Total Balance</p>
                <p className="text-xl font-bold">{formatCurrency(overview?.totalBalance || 0)}</p>
                <p className="text-xs text-muted-foreground">{utilization}% utilization</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-orange-500/10"><AlertCircle className="h-5 w-5 text-orange-500" /></div>
              <div>
                <p className="text-sm text-muted-foreground">Minimum Due</p>
                <p className="text-xl font-bold">{formatCurrency(overview?.totalMinimumPayment || 0)}</p>
                <p className="text-xs text-muted-foreground">Required to avoid late fees</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500/10"><Wallet className="h-5 w-5 text-blue-500" /></div>
              <div>
                <p className="text-sm text-muted-foreground">Desired Payments</p>
                <p className="text-xl font-bold">{formatCurrency(overview?.totalDesiredPayment || 0)}</p>
                <p className="text-xs text-muted-foreground">Your planned monthly payments</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-500/10"><Percent className="h-5 w-5 text-amber-500" /></div>
              <div>
                <p className="text-sm text-muted-foreground">Monthly Interest</p>
                <p className="text-xl font-bold">{formatCurrency(overview?.totalMonthlyInterest || 0)}</p>
                <p className="text-xs text-muted-foreground">Tax-deductible expense</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-500/10"><Hash className="h-5 w-5 text-green-500" /></div>
              <div>
                <p className="text-sm text-muted-foreground">Active Accounts</p>
                <p className="text-xl font-bold">{overview?.activeCount || 0}</p>
                <p className="text-xs text-muted-foreground">{overview?.totalCount || 0} total</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Monthly Card Load — per-card recurring obligations for cash-flow planning */}
      {cardLoads.length > 0 && (
        <Card>
          <CardContent className="p-0">
            <div className="p-4 border-b">
              <div className="flex items-baseline justify-between gap-3 flex-wrap">
                <div>
                  <h3 className="text-sm font-semibold flex items-center gap-2">
                    <Wallet className="h-4 w-4 text-blue-500" />
                    Monthly Card Load
                  </h3>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Recurring bills + subscriptions assigned to each card. Plan loading without exceeding limits.
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-muted-foreground uppercase">Total / mo</p>
                  <p className="text-lg font-bold tabular-nums">{formatCurrency(totalCardLoad)}</p>
                </div>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-muted/30">
                    <th className="text-left p-3 text-xs font-medium text-muted-foreground">Card</th>
                    <th className="text-right p-3 text-xs font-medium text-muted-foreground">Balance</th>
                    <th className="text-right p-3 text-xs font-medium text-muted-foreground">Limit</th>
                    <th className="text-right p-3 text-xs font-medium text-muted-foreground">Monthly Load</th>
                    <th className="text-right p-3 text-xs font-medium text-muted-foreground">Util Now</th>
                    <th className="text-right p-3 text-xs font-medium text-muted-foreground">After Load</th>
                  </tr>
                </thead>
                <tbody>
                  {cardLoads.map((c) => {
                    const utilNow = c.creditLimit > 0 ? (c.currentBalance / c.creditLimit) * 100 : 0;
                    const projected = c.currentBalance + c.totalMonthlyLoad;
                    const utilAfter = c.creditLimit > 0 ? (projected / c.creditLimit) * 100 : 0;
                    const headroom = c.creditLimit - projected;
                    const utilColor = utilAfter > 70
                      ? 'text-red-600'
                      : utilAfter > 30
                        ? 'text-amber-600'
                        : 'text-green-600';
                    const allocationParts: string[] = [];
                    if (c.subscriptionCount > 0) {
                      allocationParts.push(`${c.subscriptionCount} sub${c.subscriptionCount !== 1 ? 's' : ''}`);
                    }
                    if (c.expenseCount > 0) {
                      allocationParts.push(`${c.expenseCount} exp${c.expenseCount !== 1 ? 's' : ''}`);
                    }
                    return (
                      <tr key={c.id} className="border-b last:border-b-0 hover:bg-muted/20">
                        <td className="p-3">
                          <div className="font-medium text-sm">{c.name}</div>
                          <div className="text-xs text-muted-foreground">
                            {c.issuer || accountTypeLabel[c.accountType]}
                            {c.last4 && ` · ····${c.last4}`}
                            {allocationParts.length > 0 && ` · ${allocationParts.join(' · ')}`}
                            {c.allocationCount === 0 && ' · No bills allocated'}
                          </div>
                        </td>
                        <td className="p-3 text-right text-sm tabular-nums">{formatCurrency(c.currentBalance)}</td>
                        <td className="p-3 text-right text-sm text-muted-foreground tabular-nums">
                          {c.creditLimit > 0 ? formatCurrency(c.creditLimit) : '—'}
                        </td>
                        <td className="p-3 text-right text-sm font-semibold tabular-nums">
                          {formatCurrency(c.totalMonthlyLoad)}
                          <span className="text-xs text-muted-foreground font-normal">/mo</span>
                        </td>
                        <td className="p-3 text-right text-sm text-muted-foreground tabular-nums">
                          {c.creditLimit > 0 ? `${utilNow.toFixed(0)}%` : '—'}
                        </td>
                        <td className={`p-3 text-right text-sm font-semibold tabular-nums ${c.creditLimit > 0 ? utilColor : 'text-muted-foreground'}`}>
                          {c.creditLimit > 0 ? `${utilAfter.toFixed(0)}%` : '—'}
                          {c.creditLimit > 0 && (
                            <div className="text-xs text-muted-foreground font-normal">
                              {headroom >= 0 ? `${formatCurrency(headroom)} left` : `over by ${formatCurrency(-headroom)}`}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="bg-muted/30">
                    <td colSpan={3} className="p-3 text-sm font-medium">Total monthly load</td>
                    <td className="p-3 text-right font-bold tabular-nums">
                      {formatCurrency(totalCardLoad)}
                      <span className="text-xs text-muted-foreground font-normal">/mo</span>
                    </td>
                    <td colSpan={2}></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-4">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search accounts..."
            className="pl-9"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          />
        </div>
        <Select value={typeFilter} onValueChange={(v) => { setTypeFilter(v === 'all' ? '' : v); setPage(1); }}>
          <SelectTrigger className="w-44">
            <SelectValue placeholder="All types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            <SelectItem value="credit_card">Credit Card</SelectItem>
            <SelectItem value="store_card">Store Card</SelectItem>
            <SelectItem value="business_line">Business Line</SelectItem>
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v === 'all' ? '' : v); setPage(1); }}>
          <SelectTrigger className="w-36">
            <SelectValue placeholder="All statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="closed">Closed</SelectItem>
            <SelectItem value="frozen">Frozen</SelectItem>
          </SelectContent>
        </Select>
        {profiles.length > 1 && (
          <Select value={profileFilter} onValueChange={(v) => { setProfileFilter(v === 'all' ? '' : v); setPage(1); }}>
            <SelectTrigger className="w-44">
              <SelectValue placeholder="All profiles" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All profiles</SelectItem>
              {profiles.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Accounts Table */}
      {isLoading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : accounts.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <CreditCard className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground">No charge accounts found</p>
            <p className="text-sm text-muted-foreground mt-1">Add your credit cards and revolving credit accounts to track payments and interest</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <table className="w-full">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="w-8 p-3"></th>
                  <th className="text-left p-3 text-sm font-medium">Account</th>
                  <th className="text-left p-3 text-sm font-medium">Issuer</th>
                  <th className="text-left p-3 text-sm font-medium">Type</th>
                  <th className="text-right p-3 text-sm font-medium">Balance</th>
                  <th className="text-right p-3 text-sm font-medium">Limit</th>
                  <th className="text-right p-3 text-sm font-medium">APR</th>
                  <th className="text-right p-3 text-sm font-medium">Payment</th>
                  <th className="text-center p-3 text-sm font-medium">Due Day</th>
                  <th className="text-left p-3 text-sm font-medium">Status</th>
                  <th className="text-right p-3 text-sm font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {accounts.map((acct) => {
                  const monthlyInterest = acct.currentBalance > 0 && acct.interestRate > 0
                    ? acct.currentBalance * (acct.interestRate / 100) / 12
                    : 0;
                  const utilPct = acct.creditLimit > 0 ? (acct.currentBalance / acct.creditLimit) * 100 : 0;
                  const isExpanded = expandedAccountId === acct.id;
                  return (
                    <Fragment key={acct.id}>
                      <tr className="border-b hover:bg-muted/30">
                        <td className="p-3">
                          <button
                            type="button"
                            onClick={() => setExpandedAccountId(isExpanded ? null : acct.id)}
                            className="p-1 rounded hover:bg-muted"
                            aria-label={isExpanded ? 'Hide allocations' : 'Show allocations'}
                          >
                            {isExpanded
                              ? <ChevronDown className="h-4 w-4" />
                              : <ChevronRight className="h-4 w-4" />}
                          </button>
                        </td>
                        <td className="p-3 text-sm">
                          <div className="font-medium">{acct.name}</div>
                          {acct.last4 && <span className="text-xs text-muted-foreground">****{acct.last4}</span>}
                        </td>
                        <td className="p-3 text-sm">{acct.issuer || '—'}</td>
                        <td className="p-3 text-sm">{accountTypeLabel[acct.accountType] || acct.accountType}</td>
                        <td className="p-3 text-sm text-right">
                          <div className="font-medium">{formatCurrency(acct.currentBalance)}</div>
                          <span className="text-xs text-muted-foreground">{utilPct.toFixed(0)}% used</span>
                        </td>
                        <td className="p-3 text-sm text-right">{formatCurrency(acct.creditLimit)}</td>
                        <td className="p-3 text-sm text-right">{acct.interestRate}%</td>
                        <td className="p-3 text-sm text-right">
                          <div className="font-medium">{formatCurrency(acct.desiredPayment)}</div>
                          <div className="text-xs text-muted-foreground">Min: {formatCurrency(acct.minimumPayment)}</div>
                          {monthlyInterest > 0 && (
                            <span className="text-xs text-amber-600">{formatCurrency(monthlyInterest)} int.</span>
                          )}
                        </td>
                        <td className="p-3 text-sm text-center">
                          <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-muted text-xs font-medium">
                            {acct.paymentDueDay}
                          </span>
                        </td>
                        <td className="p-3">
                          <Badge variant={statusVariant[acct.status] || 'secondary'}>{acct.status}</Badge>
                          {acct.autopayEnabled && (
                            <span className="ml-1 text-xs text-green-600">AP</span>
                          )}
                        </td>
                        <td className="p-3 text-right">
                          <Button variant="ghost" size="sm" onClick={() => openEdit(acct)}>Edit</Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-destructive"
                            onClick={() => { if (confirm('Delete this charge account?')) deleteMutation.mutate(acct.id); }}
                          >
                            Delete
                          </Button>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr>
                          <td colSpan={11} className="p-0">
                            <AllocationsPanel accountId={acct.id} creditLimit={acct.creditLimit} />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent key={editingAccount?.id || 'new'} className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingAccount ? 'Edit Charge Account' : 'New Charge Account'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name">Account Name *</Label>
                <Input name="name" id="name" required placeholder="e.g., Capital One Business" defaultValue={editingAccount?.name || ''} />
              </div>
              <div>
                <Label htmlFor="issuer">Issuer</Label>
                <Input name="issuer" id="issuer" placeholder="e.g., Capital One" defaultValue={editingAccount?.issuer || ''} />
              </div>
            </div>
            <div>
              <Label htmlFor="description">Description</Label>
              <Input name="description" id="description" defaultValue={editingAccount?.description || ''} />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label htmlFor="accountType">Account Type</Label>
                <select
                  name="accountType"
                  id="accountType"
                  defaultValue={editingAccount?.accountType || 'credit_card'}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="credit_card">Credit Card</option>
                  <option value="store_card">Store Card</option>
                  <option value="business_line">Business Line</option>
                </select>
              </div>
              <div>
                <Label htmlFor="status">Status</Label>
                <select
                  name="status"
                  id="status"
                  defaultValue={editingAccount?.status || 'active'}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="active">Active</option>
                  <option value="closed">Closed</option>
                  <option value="frozen">Frozen</option>
                </select>
              </div>
              <div>
                <Label htmlFor="last4">Last 4 Digits</Label>
                <Input name="last4" id="last4" maxLength={4} placeholder="1234" defaultValue={editingAccount?.last4 || ''} />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label htmlFor="creditLimit">Credit Limit</Label>
                <Input type="number" name="creditLimit" id="creditLimit" step="0.01" min="0" defaultValue={editingAccount?.creditLimit || ''} />
              </div>
              <div>
                <Label htmlFor="currentBalance">Current Balance *</Label>
                <Input type="number" name="currentBalance" id="currentBalance" step="0.01" min="0" required defaultValue={editingAccount?.currentBalance || ''} />
              </div>
              <div>
                <Label htmlFor="interestRate">APR %</Label>
                <Input type="number" name="interestRate" id="interestRate" step="0.01" min="0" max="100" defaultValue={editingAccount?.interestRate || ''} />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label htmlFor="minimumPayment">Minimum Payment</Label>
                <Input type="number" name="minimumPayment" id="minimumPayment" step="0.01" min="0" defaultValue={editingAccount?.minimumPayment || ''} />
              </div>
              <div>
                <Label htmlFor="desiredPayment">Desired Payment *</Label>
                <Input type="number" name="desiredPayment" id="desiredPayment" step="0.01" min="0" required defaultValue={editingAccount?.desiredPayment || ''} />
              </div>
              <div>
                <Label htmlFor="paymentDueDay">Due Day (1-31) *</Label>
                <Input type="number" name="paymentDueDay" id="paymentDueDay" min="1" max="31" required defaultValue={editingAccount?.paymentDueDay || 1} />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="statementCloseDay">Statement Close Day</Label>
                <Input type="number" name="statementCloseDay" id="statementCloseDay" min="1" max="31" defaultValue={editingAccount?.statementCloseDay || ''} />
              </div>
              <div>
                <Label htmlFor="businessProfileId">Business Profile</Label>
                <select
                  name="businessProfileId"
                  id="businessProfileId"
                  defaultValue={editingAccount?.businessProfileId || ''}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="">None (General)</option>
                  {profiles.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
            </div>

            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  name="autopayEnabled"
                  id="autopayEnabled"
                  defaultChecked={editingAccount?.autopayEnabled || false}
                  className="h-4 w-4"
                />
                <Label htmlFor="autopayEnabled">Autopay Enabled</Label>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  name="isInterestDeductible"
                  id="isInterestDeductible"
                  defaultChecked={editingAccount?.isInterestDeductible ?? true}
                  className="h-4 w-4"
                />
                <Label htmlFor="isInterestDeductible">Interest Tax Deductible</Label>
              </div>
            </div>

            <div className="rounded-md border bg-muted/30 p-3">
              <p className="text-xs text-muted-foreground">
                Tax form line: <span className="font-medium text-foreground">Other Interest</span>
                <span className="ml-1">(Schedule C Line 16b · Form 1120 Line 18)</span>
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Credit card interest is auto-classified when the linked expense row is created.
              </p>
            </div>

            <div>
              <Label htmlFor="notes">Notes</Label>
              <Textarea name="notes" id="notes" rows={2} defaultValue={editingAccount?.notes || ''} />
            </div>

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
                {(createMutation.isPending || updateMutation.isPending) && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {editingAccount ? 'Update' : 'Create'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
